import AuthServices from './authService'; // Assuming AuthServices is properly implemented
import { toast } from 'react-toastify';

import {
  PurchaseOrder,
  CartItem,
  OrderDetails,
  OrderResponse,
  Checkout,
  SingleOrderDetails,
} from '../interfaces'; // Assuming you have the PurchaseOrder type from previous code
// import store from '../store/store';

const baseUrl = import.meta.env.VITE_BASEURL as string;

const emptyCart = {
  purchaseCode: '',
  purchases: [],
  orderId: 0,
  totalAmount: 0,
  totalAmountWithVAT: 0,
  vat: 0,
  message: '',
};

const updatePurchaseCart = async (
  cartItem: CartItem
): Promise<PurchaseOrder> => {
  const { CartLineItems, purchaseCode } = cartItem;
  const storeId = localStorage.getItem('storeId');
  const response = await fetch(`${baseUrl}cart`, {
    method: 'PATCH',
    headers: {
      ...AuthServices.getAuthHeaders(),
      'x-store-id': String(storeId),
    },
    body: JSON.stringify({
      CartLineItems,
      purchaseCode,
      guestSessionId: localStorage.getItem('guestSessionID'),
      storeId: localStorage.getItem('storeId'),
    }),
  })
    .then(async (resp) => {
      handleApiResponse(resp);
      return resp.json();
    })
    .then((result) => {
      return result;
    });
  if (response.data) {
    return response.data;
  } else {
    return emptyCart;
  }
};

const handleApiResponse = async (
  response: Response,
  defaultErrorMsg = 'An error occurred'
) => {
  const status = response?.status;
  if (status === 200 || status === 201) {
    return;
  }
  if (status === 401) {
    toast('Unauthorized or Expired Session');
    console.warn('Unauthorized or Expired Session');
    AuthServices.logout();
    throw new Error('Unauthorized or Expired Session');
  } else {
    const errorBody = await response.json();
    console.log(errorBody);
    toast.error(errorBody?.message || errorBody?.Message || defaultErrorMsg);
    throw new Error(
      `${errorBody?.message || errorBody?.Message || defaultErrorMsg}`
    );
  }
};

// GET request to fetch purchase cart
const getPurchaseCart = async (): Promise<PurchaseOrder> => {
  const accessToken = AuthServices.getAccessToken(); // Get the token from your AuthService
  const storeId = localStorage.getItem('storeId');
  const result = await fetch(`${baseUrl}cart`, {
    method: 'GET',
    headers: {
      ...AuthServices.getAuthHeaders(),
      'x-store-id': String(storeId),
    },
  });
  await handleApiResponse(result);
  const response = await result.json();

  if (!accessToken) {
    const GUEST_PURCHASE_CODE = response?.data?.purchaseCode;
    if (GUEST_PURCHASE_CODE) {
      localStorage.setItem('guestPurchaseCode', GUEST_PURCHASE_CODE);
    }
  }

  if (response.data) {
    return response.data;
  } else {
    return emptyCart;
  }
};

// POST transfer GUEST CART
const transferGuestCart = async (): Promise<PurchaseOrder> => {
  const storeId = localStorage.getItem('storeId');
  const guestSessionId = localStorage.getItem('guestSessionID');
  const purchaseCode = localStorage.getItem('guestPurchaseCode');
  const result = await fetch(`${baseUrl}cart/guest/transfer`, {
    method: 'POST',
    headers: {
      ...AuthServices.getAuthHeaders(),
      'x-store-id': String(storeId),
    },
    body: JSON.stringify({
      guestSessionId,
      purchaseCode,
    }),
  });
  await handleApiResponse(result);
  const response = await result.json();

  if (response.data) {
    return response.data;
  } else {
    return emptyCart;
  }
};

const getOrders = async (): Promise<OrderResponse> => {
  const accessToken = AuthServices.getAccessToken(); // Get the token from your AuthService
  if (accessToken) {
    const response = await fetch(`${baseUrl}customer/orders`, {
      method: 'GET',
      headers: AuthServices.getAuthHeaders(),
    }); //fetch remote cart
    await handleApiResponse(response);
    const data = await response.json();
    return data; //Local cart is returned
  } else {
    return {
      message: 'You are required to login',
    };
  }
};

const getStore = async () => {
  const accessToken = AuthServices.getAccessToken(); // Get the token from your AuthService
  if (accessToken) {
    const response = await fetch(`${baseUrl}store`, {
      method: 'GET',
      headers: AuthServices.getAuthHeaders(),
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.data;
  }
};

const getSingleOrder = async (orderId: number): Promise<SingleOrderDetails> => {
  const response = await fetch(`${baseUrl}order?orderId=${orderId}`, {
    method: 'GET',
    headers: AuthServices.getAuthHeaders(),
  }); //fetch remote cart
  await handleApiResponse(response);
  const data = await response.json();
  return data.data; //Local cart is returned
};

const getSingleOrderByPurchaseCode = async (
  purchaseCode: string
): Promise<SingleOrderDetails> => {
  const response = await fetch(
    `${baseUrl}order/by-purchase-code?purchaseCode=${purchaseCode}`,
    {
      method: 'GET',
      headers: AuthServices.getAuthHeaders(),
    }
  ); //fetch remote cart
  await handleApiResponse(response);
  const data = await response.json();
  return data.data; //Local cart is returned
};

//substore-landmark
const getLandmarks = async (): Promise<OrderResponse> => {
  const response = await fetch(`${baseUrl}store/delivery-locations`, {
    method: 'GET',
    headers: AuthServices.getAuthHeaders(),
  }); //fetch remote cart
  await handleApiResponse(response);
  const data = await response.json();
  return data.data;
};

// updateOrderDetails
const updateOrderDetails = async (
  orderDetails: OrderDetails
): Promise<PurchaseOrder> => {
  // const accessToken = AuthServices.getAccessToken(); // Get the token from your AuthService
  const { reference, orderType, orderAddress, deliveryInstructions } =
    orderDetails;
  const response = await fetch(`${baseUrl}storefront/order-type`, {
    method: 'POST',
    headers: AuthServices.getAuthHeaders(),
    body: JSON.stringify({
      reference,
      orderType,
      orderAddress,
      deliveryInstructions,
    }),
  });

  await handleApiResponse(response);
  const data = await response.json();
  return data;
};

const webStorefrontCheckout = async (
  checkoutDetails: Checkout
): Promise<unknown> => {
  const response = await fetch(`${baseUrl}cart/checkout/web-storefront`, {
    method: 'POST',
    headers: {
      ...AuthServices.getAuthHeaders(),
    },
    body: JSON.stringify(checkoutDetails),
  });
  await handleApiResponse(response);
  const data = await response.json();
  return data;
};

const webStorefrontAnonymousCheckout = async (
  checkoutDetails: Checkout
): Promise<unknown> => {
  const response = await fetch(`${baseUrl}cart/checkout/web-storefront`, {
    method: 'POST',
    headers: {
      ...AuthServices.getAuthHeaders(),
    },
    body: JSON.stringify(checkoutDetails),
  });
  await handleApiResponse(response);
  const data = await response.json();
  return data;
};

const addAddressDetails = async (
  addressData: {
    sessionId: string;
    body: {
      address: string;
      latitude: string;
      longitude: string;
      deliveryPhoneNumber: string;
      deliveryInstructions: string;
      state: string;
      deliveryLocationId?: number;
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const response = await fetch(
    `${baseUrl}mimic/session/address?sessionId=${addressData.sessionId}`,
    {
      method: 'POST',
      headers: AuthServices.getAuthHeaders(),
      body: JSON.stringify(addressData.body),
    }
  );

  await handleApiResponse(response);

  const data = await response.json();
  return data;
};

export default {
  addAddressDetails,
  getPurchaseCart,
  updatePurchaseCart,
  updateOrderDetails,
  webStorefrontCheckout,
  webStorefrontAnonymousCheckout,
  getOrders,
  getLandmarks,
  getSingleOrder,
  getSingleOrderByPurchaseCode,
  getStore,
  transferGuestCart,
};
